import { createColumnHelper } from "@tanstack/react-table";
import type { TFunction } from "react-i18next";

import { useGetSpecialPricesQuery } from "src/api";
import { SelectColumnFilter, TextColumnFilter } from "src/components";
import { createTableModule, LongTextCell, ParentRow, TableNames } from "src/table";
import {
    type ExpandTableSpecialPricesType,
    type ProductBrandWithSpecialPricesDto,
    type SpecialPriceDto,
    isProductBrandWithSpecialPricesDto,
    isSpecialPriceDto,
    SpecialPriceSourceEnum,
    SpecialPriceStatusEnum,
} from "src/types";
import { currencyFormatter, dateTimeFormatter, enumToLocalizedOptions, getTranslatedEnumValue, percentageFormatter } from "src/utils";
import { ShipTosCell } from "./ShipTosCell";

const columnHelper = createColumnHelper<ProductBrandWithSpecialPricesDto | SpecialPriceDto>();
const createColumns = (t: TFunction) => [
    columnHelper.accessor("productPackageName", {
        id: "productPackageName",
        header: t("specialPrices.table.name") as string,
        cell: ({ row }) =>
            isProductBrandWithSpecialPricesDto(row.original) ? <ParentRow>{row.original.name}</ParentRow> : row.original.productPackageName,
        meta: { width: 350, minWidth: 150, filterComponent: TextColumnFilter, expandedRowColspan: 3 },
    }),
    columnHelper.accessor("specialPriceInclMargin", {
        id: "specialPriceInclMargin",
        enableColumnFilter: false,
        header: t("specialPrices.table.specialPrice") as string,
        cell: ({ row }) => isSpecialPriceDto(row.original) && currencyFormatter(row.original.specialPriceInclMargin),
        meta: { minWidth: 100, align: "end" },
    }),
    columnHelper.accessor("validFrom", {
        id: "validFrom",
        enableColumnFilter: false,
        header: t("specialPrices.table.from") as string,
        cell: ({ row }) => isSpecialPriceDto(row.original) && dateTimeFormatter(row.original.validFrom),
        meta: { minWidth: 100 },
    }),
    columnHelper.accessor("validTo", {
        id: "validTo",
        enableColumnFilter: false,
        header: t("specialPrices.table.to") as string,
        cell: ({ row }) => isSpecialPriceDto(row.original) && dateTimeFormatter(row.original.validTo),
        meta: { minWidth: 100 },
    }),
    columnHelper.accessor("specialPriceMargin", {
        id: "specialPriceMargin",
        enableColumnFilter: false,
        header: t("specialPrices.table.margin") as string,
        cell: ({ row }) => isSpecialPriceDto(row.original) && percentageFormatter(row.original.specialPriceMargin),
        meta: { minWidth: 100, align: "end" },
    }),
    columnHelper.accessor("contractId", {
        id: "contractId",
        enableColumnFilter: false,
        header: t("specialPrices.table.contract") as string,
        meta: { minWidth: 100, align: "end" },
    }),
    columnHelper.accessor("createdBy", {
        id: "createdBy",
        enableColumnFilter: false,
        header: t("specialPrices.table.createdBy") as string,
        meta: { minWidth: 100, collapse: true },
    }),
    columnHelper.accessor("azNote", {
        id: "azNote",
        enableColumnFilter: false,
        header: t("specialPrices.table.note") as string,
        cell: ({ row }) => isSpecialPriceDto(row.original) && <LongTextCell text={row.original.azNote ?? ""} charLimit={8} />,
        meta: { minWidth: 100, collapse: true },
    }),
    columnHelper.accessor("status", {
        id: "status",
        enableColumnFilter: true,
        header: t("specialPrices.table.status") as string,
        cell: (cellContext) => {
            const specialPrice = isSpecialPriceDto(cellContext.row.original) ? cellContext.row.original : undefined;
            if (!specialPrice) {
                return null;
            }

            return getTranslatedEnumValue("specialPriceStatus", cellContext.getValue() as string);
        },
        meta: {
            minWidth: 100,
            collapse: true,
            filterComponent: SelectColumnFilter,
            options: enumToLocalizedOptions("specialPriceStatus", SpecialPriceStatusEnum),
        },
    }),
    columnHelper.accessor("applyToShipTos", {
        id: "applyToShipTos",
        enableColumnFilter: false,
        header: t("specialPrices.table.applied") as string,
        cell: ({ row }) => {
            if (isSpecialPriceDto(row.original) && row.original.specialPriceSource === SpecialPriceSourceEnum.ShipTo) {
                return row.original.applyToShipTos?.[0].name ?? "";
            }

            return isSpecialPriceDto(row.original) && <ShipTosCell shipTos={row.original.applyToShipTos ?? []} />;
        },
        meta: { align: "end", collapse: true },
    }),
    columnHelper.accessor("specialPriceSource", {
        id: "specialPriceSource",
        header: t("specialPrices.table.source") as string,
        cell: (cellContext) =>
            isSpecialPriceDto(cellContext.row.original) && getTranslatedEnumValue("specialPriceSource", cellContext.getValue() as string),
        meta: {
            minWidth: 120,
            collapse: true,
            filterComponent: SelectColumnFilter,
            options: enumToLocalizedOptions("specialPriceSource", SpecialPriceSourceEnum),
        },
    }),
    columnHelper.accessor("bulkId", {
        id: "bulkId",
        header: t("specialPrices.table.bulkId") as string,
        meta: {
            filterComponent: TextColumnFilter,
            minWidth: 90,
        },
    }),
];

export const tableModule = createTableModule<ExpandTableSpecialPricesType>(
    TableNames.payerSpecialPrices,
    useGetSpecialPricesQuery,
    createColumns,
);
